import { ApolloError } from '@apollo/client';

import {
  FormModal,
  FormModalProps,
} from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions/FormModalActions';
import {
  FeedbackMessages,
  useFeedback,
} from '@/components/notifications/Feedback/useFeedback';
import {
  FormProvider,
  useForm,
  useFormContext,
  useSubmitSuccessHandler,
} from '@/components/react-hook-form';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';
import {
  TenantKind,
  TenantProductTier,
  TenantUsageBucket,
} from '@/types/schema';

import { CreateTenantForm } from '../AdminTenantsPage/CreateTenantModal/CreateTenantForm';
import { CreateTenantFormShape } from '../AdminTenantsPage/CreateTenantModal/CreateTenantForm.types';
import {
  Operations,
  useAdminOrganizationsPageCreateLuminaryOrganizationMemberTenantMutation,
} from './graphql/AdminOrganizationsPage.generated';

type CreateOrganizationMemberTenantModalProps = Pick<
  FormModalProps,
  'isOpen' | 'onClose'
>;

function CreateOrganizationMemberTenantModalContent({
  isOpen,
  onClose,
}: CreateOrganizationMemberTenantModalProps) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { formRef, handleSave } = useFormSaveHandler();
  const { handleSubmit, reset, shouldBlockNavigation, formState } =
    useFormContext<CreateTenantFormShape>();
  const [createMemberTenant] =
    useAdminOrganizationsPageCreateLuminaryOrganizationMemberTenantMutation({
      refetchQueries: [Operations.Query.AdminOrganizationsPage],
      awaitRefetchQueries: true,
    });

  const closeModal = () => {
    reset();
    onClose();
  };

  useSubmitSuccessHandler(() => {
    showFeedback('Member tenant created', { variant: 'success' });
    closeModal();
  });

  const onSubmit = handleSubmit(async (formData) => {
    try {
      await createMemberTenant({
        variables: {
          input: {
            tenant: {
              addonAssetProviderIntegrationEnabled:
                formData.addonAssetProviderIntegrationEnabled,
              kind: formData.kind,
              name: formData.name,
              productTier: formData.tier,
              subdomain: formData.subdomain,
              trialEndDate: formData.trialEndDate,
              usageBucket: formData.usageBucket,
            },
          },
        },
      });
    } catch (error) {
      const userFacingMessages =
        error instanceof ApolloError ? getUserFacingErrorMessages(error) : [];
      showFeedback(
        userFacingMessages.length > 0
          ? userFacingMessages.join('. ')
          : FeedbackMessages.formSaveError
      );
      reportError(
        'Could not create organization member tenant',
        error as Error
      );
      throw error;
    }
  });

  return (
    <FormModal
      isOpen={isOpen}
      onClose={closeModal}
      heading="Create member tenant"
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          <FormModalActions.CancelButton onClick={closeModal} />
          <FormModalActions.SaveButton onClick={handleSave}>
            Create member tenant
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <CreateTenantForm nameLabel="Member tenant display name" />
      </form>
    </FormModal>
  );
}

export function CreateOrganizationMemberTenantModal(
  props: CreateOrganizationMemberTenantModalProps
) {
  const formMethods = useForm<CreateTenantFormShape>({
    defaultValues: {
      name: '',
      subdomain: '',
      addonAssetProviderIntegrationEnabled: false,
      tier: TenantProductTier.Pro,
      trialEndDate: null,
      kind: '' as TenantKind,
      usageBucket: '' as TenantUsageBucket,
    },
  });

  return (
    <FormProvider {...formMethods}>
      <CreateOrganizationMemberTenantModalContent {...props} />
    </FormProvider>
  );
}
